import api, { wellControlInstance } from '../../services/api';
import { getAPIBaseURL } from '../../utilities/BaseURLUtility';
import { sanitizeForLog } from '../../utilities/logSanitizer';
import { generateNormalizedCacheKey } from '../../utilities/DateNormalizationUtil';

const baseUrl = getAPIBaseURL();

// Fetch Card Dates
export interface FetchCardDatesResponseValues {
  date: string;
  cardTypeId: string;
  cardTypeName: string;
  id: number;
}

export interface FetchCardDatesResponse {
  values: FetchCardDatesResponseValues[];
}

const cardDatesRequests = new Map<string, Promise<FetchCardDatesResponse>>();
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const fetchCardDatesAsync = async (assetId: string): Promise<FetchCardDatesResponse> => {
  if (cardDatesRequests.has(assetId)) {
    const cachedPromise = cardDatesRequests.get(assetId)!;
    return cachedPromise.then(async (result) => {
      await delay(10);
      return result;
    });
  }

  const fetchPromise = (async () => {
    try {
      const response = await api.get('Cards', {
        params: { assetId },
        headers: {
          'content-type': 'application/json',
        },
      });

      return response?.data as FetchCardDatesResponse;
    } catch (error) {
      console.error('Error fetching rod lift analysis card dates', error);
      return { values: [] };
    } finally {
      cardDatesRequests.delete(assetId);
    }
  })();

  cardDatesRequests.set(assetId, fetchPromise);
  return fetchPromise;
};

export interface CardDataItem {
  cardDate: string;
  cardTypeDescription: string;
  cardTypeId: string | null;
  downHoleStroke: number;
  pumpEfficiency: number | null;
  pumpFillage: number | null;
  pumpFillageSetpoint: number | null;
  secondaryFillageSetpoint: number | null;
  inferredProduction: number | null;
  runtime: number | null;
  strokesPerMinute: number | null;
  minPolishedRodLoad: number | null;
  maxPolishedRodLoad: number | null;
  fluidPound: number | null;
  buoyantRodWeight: number | null;
  dryRodWeight: number | null;
  pumpFriction: number | null;
  poFluidLoad: number | null;
  upliftOpportunity: boolean;
  rodsOverloaded: boolean | null;
  pumpTagging: boolean;
  gearboxOverloaded: boolean;
  svLeak: number | null;
  standingValveLeak: boolean;
}

export interface FetchCardDataResponse {
  assetGuid: string;
  cardDataItems: CardDataItem[];
  id: string;
  dateCreated: string;
}

const cardDataRequests = new Map<string, Promise<FetchCardDataResponse>>();
const fetchCardDataAsync = async (
  assetId: string,
  startDate: string,
  endDate: string,
): Promise<FetchCardDataResponse> => {
  const cacheKey = `${assetId}_${startDate}_${endDate}`;

  if (cardDataRequests.has(cacheKey)) {
    return cardDataRequests.get(cacheKey)!;
  }

  const fetchPromise = (async () => {
    try {
      const response = await api.get<FetchCardDataResponse>(`Cards/GetCards/${assetId}`, {
        params: { startDate, endDate },
        headers: {
          'Content-Type': 'application/json',
          'x-api-version': '2.0',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch fetchCardDataAsync using dates:', error);
      return {
        assetGuid: '',
        cardDataItems: [],
        id: '',
        dateCreated: '',
      };
    } finally {
      cardDataRequests.delete(cacheKey);
    }
  })();

  cardDataRequests.set(cacheKey, fetchPromise);
  return fetchPromise;
};

// Fetch Card Coordinates
export interface FetchCardCoordinatesCoordinate {
  x: number;
  y: number;
}

export interface FetchCardCoordinatesObject {
  id: number;
  name: string;
  coordinates: FetchCardCoordinatesCoordinate[];
}

export interface FetchCardCoordinatesResponse {
  values: FetchCardCoordinatesObject[];
}

const fetchCardCoordinatesAsync = async (assetId: string, cardDate: string) => {
  const queryParams = {
    assetId,
    cardDate,
  };

  return await api
    .get('Analytics/RLAnalysis/RLCoordinates', {
      params: queryParams,
      headers: {
        'content-type': 'application/json',
      },
    })
    .then(function (response: any) {
      if (response) {
        return response.data as FetchCardCoordinatesResponse;
      } else {
        return undefined;
      }
    });
};

const fetchCardCoordinatesBatchAsync = async (assetId: string, cardDates: string[]) => {
  const payload = {
    assetId: assetId,
    cardDates: cardDates,
  };

  return await api
    .post('/Analytics/RLAnalysis/RLCoordinates/Batch', payload, {
      headers: {
        'content-type': 'application/json',
      },
    })
    .then(function (response: any) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
};

// Fetch Analysis Results
export interface AnalysisInputOutputResponseValues {
  id: string;
  name: string;
  displayName: string;
  value: string;
  displayValue: string;
  dataTypeId: number;
  measurementAbbreviation: string;
  sourceId: number;
}

export interface FetchAnalysisResponseValues {
  input: AnalysisInputOutputResponseValues[];
  output: AnalysisInputOutputResponseValues[];
}

export interface FetchAnalysisResponse {
  values: FetchAnalysisResponseValues;
}

const fetchAnalysisAsync = async (assetId: string, cardDate: string) => {
  try {
    const queryParams = {
      assetId,
      cardDate,
    };

    return await api
      .get('Analytics/RLAnalysis', {
        params: queryParams,
        headers: {
          'content-type': 'application/json',
        },
      })
      .then(function (response: any) {
        if (response) {
          return response.data as FetchAnalysisResponse;
        } else {
          return undefined;
        }
      });
  } catch (error) {
    console.error('Error fetching rod lift analysis', error);
    return undefined;
  }
};

const fetchCardDetailsInitialStateAsync = async () => {
  try {
    return await api
      .get('CardData', {
        headers: {
          'content-type': 'application/json',
        },
      })
      .then(function (response: any) {
        if (response) {
          return response.data as FetchAnalysisResponse;
        }
        return undefined;
      });
  } catch (error) {
    console.error('Error fetching initial state:', error);
    return undefined;
  }
};

// Streaming Card Data for v3
export interface StreamingCardDataChunk {
  id: string;
  dateCreated: string;
  assetGuid: string;
  cardDataItems: CardDataItem[];
}

export interface StreamingCardDataResponse {
  assetGuid: string;
  cardDataItems: CardDataItem[];
  id: string;
  dateCreated: string;
  isComplete: boolean;
  chunkIndex: number;
}

const streamingCardDataRequests = new Map<string, AbortController>();

const fetchCardDataStreamAsync = async (
  assetId: string,
  startDate: string,
  endDate: string,
  onChunkReceived: (chunk: StreamingCardDataResponse) => void,
): Promise<void> => {
  const cacheKey = generateNormalizedCacheKey(assetId, startDate, endDate);

  // Check if a request is already in progress - prevent duplicates
  if (streamingCardDataRequests.has(cacheKey)) {
    console.log(`Request already in progress for cacheKey: ${cacheKey}`);
    return;
  }

  // Cancel any existing requests for the same assetId (different date ranges)
  for (const [existingKey, existingController] of streamingCardDataRequests.entries()) {
    if (existingKey.startsWith(`${assetId}_`)) {
      console.log(`Cancelling existing request: ${existingKey}`);
      existingController.abort();
      streamingCardDataRequests.delete(existingKey);
    }
  }

  const abortController = new AbortController();
  streamingCardDataRequests.set(cacheKey, abortController);
  console.log(`Starting new request with cacheKey: ${cacheKey}`);

  // Note: We use native fetch() instead of api.get() (axios) because:
  // 1. Axios buffers the entire response before returning, which doesn't work for streaming
  // 2. We need access to response.body.getReader() for incremental chunk processing
  // 3. Native fetch provides ReadableStream support required for streaming data

  // TODO-REMOVE :  Start timing :
  const streamStartTime = Date.now();
  let firstChunkTime: number | null = null;
  let lastChunkTime: number = streamStartTime;
  console.log('Starting stream request at:', new Date(streamStartTime).toISOString());
  // TODO-REMOVE :  Start timing
  try {
    const streamUrlTest = localStorage.getItem('StreamUrl');
    let streamUrl = 'GetCardsStream';
    if (streamUrlTest) {
      streamUrl = streamUrlTest;
    }
    const response = await fetch(
      `${baseUrl}/Cards/${streamUrl}/${assetId}?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
      {
        method: 'GET',
        signal: abortController.signal,
        headers: {
          'content-type': 'application/json',
          'x-api-version': '2.0',
        },
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('ReadableStream not supported');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let chunkIndex = 0;
    let isFirstChunk = true;
    let isLastChunk = false;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Process complete JSON objects from the buffer
      let startIndex = 0;
      let braceCount = 0;
      let inString = false;
      let escapeNext = false;

      for (let i = 0; i < buffer.length; i++) {
        const char = buffer[i];

        if (escapeNext) {
          escapeNext = false;
          continue;
        }

        if (char === '\\') {
          escapeNext = true;
          continue;
        }

        if (char === '"' && !escapeNext) {
          inString = !inString;
          continue;
        }

        if (!inString) {
          if (char === '{') {
            if (braceCount === 0) {
              startIndex = i;
            }
            braceCount++;
          } else if (char === '}') {
            braceCount--;
            if (braceCount === 0) {
              // Found complete JSON object
              let jsonStr = buffer.substring(startIndex, i + 1);

              // Clean up the JSON string (remove leading [ and trailing ])
              if (isFirstChunk && jsonStr.startsWith('[')) {
                jsonStr = jsonStr.substring(1);
                isFirstChunk = false;
              }

              // Check if this is the last chunk (ends with ])
              const remainingBuffer = buffer.substring(i + 1).trim();
              const isClosingBracket = remainingBuffer.startsWith(']') || buffer.trim().endsWith(']');
              if (isClosingBracket) {
                isLastChunk = true;
              }

              try {
                const chunkData = JSON.parse(jsonStr) as StreamingCardDataChunk;

                // TODO-REMOVE :  Track timing for each chunk
                const chunkReceivedTime = Date.now();
                const timeSinceStart = chunkReceivedTime - streamStartTime;
                const timeSinceLastChunk = chunkReceivedTime - lastChunkTime;
                if (firstChunkTime === null) {
                  firstChunkTime = chunkReceivedTime;
                  console.log('First chunk received:', {
                    timeToFirstChunk: `${timeSinceStart}ms`,
                    timestamp: new Date(chunkReceivedTime).toISOString(),
                    itemsInChunk: chunkData.cardDataItems?.length || 0,
                  });
                } else {
                  console.log('Chunk received:', {
                    chunkIndex: chunkIndex + 1,
                    timeSinceStart: `${timeSinceStart}ms`,
                    timeSinceLastChunk: `${timeSinceLastChunk}ms`,
                    timestamp: new Date(chunkReceivedTime).toISOString(),
                    itemsInChunk: chunkData.cardDataItems?.length || 0,
                    isLastChunk,
                  });
                }
                lastChunkTime = chunkReceivedTime;
                // TODO-REMOVE :  Track timing for each chunk

                const processedChunk: StreamingCardDataResponse = {
                  assetGuid: chunkData.assetGuid,
                  cardDataItems: chunkData.cardDataItems,
                  id: chunkData.id,
                  dateCreated: chunkData.dateCreated,
                  isComplete: isLastChunk,
                  chunkIndex: chunkIndex++,
                };

                onChunkReceived(processedChunk);
              } catch (parseError) {
                console.error('Error parsing JSON chunk.', {
                  error: sanitizeForLog(String(parseError)),
                  json: sanitizeForLog(jsonStr),
                });
              }

              // Remove processed part from buffer
              buffer = buffer.substring(i + 1);
              i = -1; // Reset loop
              startIndex = 0;
            }
          }
        }
      }
    }

    // TODO-REMOVE : Stream ended - ensure we mark completion if we haven't already
    const streamEndTime = Date.now();
    const totalStreamTime = streamEndTime - streamStartTime;
    const streamingDuration = firstChunkTime ? streamEndTime - firstChunkTime : totalStreamTime;
    const timeToFirstChunk = firstChunkTime ? firstChunkTime - streamStartTime : 0;

    console.log('Stream completed:', {
      totalTime: `${totalStreamTime}ms (${(totalStreamTime / 1000).toFixed(2)}s)`,
      timeToFirstChunk: `${timeToFirstChunk}ms`,
      streamingDuration: `${streamingDuration}ms`,
      totalChunks: chunkIndex,
      endTimestamp: new Date(streamEndTime).toISOString(),
      wasLastChunkMarked: isLastChunk,
    });
    // TODO-REMOVE : Stream ended - ensure we mark completion if we haven't already

    if (!isLastChunk) {
      console.log('Stream ended without explicit last chunk marker - marking as complete');
      onChunkReceived({
        assetGuid: '',
        cardDataItems: [],
        id: '',
        dateCreated: '',
        isComplete: true,
        chunkIndex: chunkIndex,
      });
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('Streaming request was aborted');
    } else {
      console.error('Error in streaming fetch:', error);
    }
  } finally {
    streamingCardDataRequests.delete(cacheKey);
  }
};

export {
  fetchCardDatesAsync,
  fetchCardCoordinatesAsync,
  fetchAnalysisAsync,
  fetchCardDetailsInitialStateAsync,
  fetchCardDataAsync,
  fetchCardDataStreamAsync,
  fetchCardCoordinatesBatchAsync,
};

export interface CardTypeResponse {
  values: Array<{
    cardTypeCode: string;
    cardTypeDescription: string;
  }>;
  id: string;
  dateCreated: string;
}

// Fetch Card Types for a given assetId
export const fetchCardTypesAsync = async (assetId: string): Promise<CardTypeResponse> => {
  try {
    const response = await wellControlInstance.get(`/wellcontrol/GetCardTypes`, {
      params: { assetId },
      headers: {
        'content-type': 'application/json',
        'x-api-version': '2.0',
      },
    });
    return response.data as CardTypeResponse;
  } catch (error) {
    console.error('Error fetching card types:', error);
    return {
      values: [],
      id: assetId,
      dateCreated: new Date().toISOString(),
    };
  }
};

export interface PostGetCardRequest {
  assetId: string;
  cardType: string;
  socketId: string;
}

export interface PostGetCardResponse {
  status: number;
  responseMessage: string;
}

export const postGetCardAsync = async (payload: PostGetCardRequest): Promise<PostGetCardResponse> => {
  try {
    const response = await wellControlInstance.post(`/wellcontrol/GetCard`, payload, {
      headers: {
        'content-type': 'application/json',
      },
    });
    return {
      status: response.status,
      responseMessage: response.data?.responseMessage || 'Transaction sent successfully',
    };
  } catch (error: any) {
    console.error('Error posting GetCard:', error);
    return {
      status: error?.response?.status || 500,
      responseMessage: error?.response?.data?.responseMessage || 'Failed to send transaction',
    };
  }
};
